import { ThemeEvents } from '@theme/events';

/**
 * Keeps the standalone product AR showcase aligned with the selected variant.
 * The section response contains the selected variant's media metafield and
 * registers the lower CTA as a native Shopify XR control for that model.
 * The model loads as the section approaches the viewport so it is interactive
 * without requiring a separate play action.
 * Camera angle and zoom limits come from snippets/product-media.liquid (one place
 * for both viewers: beds get a perspective foot-corner view, other pieces the
 * near-orthographic 3/4 CAD look). Once loaded, the viewer loops the model's
 * built-in animation (doors, shelves, drawer) if the GLB has one.
 * Products in FRONT_VIEW_PRODUCTS (tall corner shelves) get a near-frontal camera
 * instead, so the piece fills the frame. The animation first holds on its final
 * frame so the finished, decorated piece is what shoppers see first.
 */
const FRONT_VIEW_PRODUCTS = ['10827437211940']; // KS02 kệ góc lượn sóng
class ProductArShowcase extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {MutationObserver | undefined} */
  #observer;

  /** @type {IntersectionObserver | undefined} */
  #modelObserver;

  #syncQueued = false;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    document.addEventListener(ThemeEvents.productArVariantUpdate, this.#handleVariantUpdate, { signal });

    const main = document.querySelector('main');
    if (main) {
      this.#observer = new MutationObserver(this.#scheduleButtonSync);
      this.#observer.observe(main, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['hidden', 'data-shopify-xr-hidden', 'data-shopify-model3d-id'],
      });
    }

    this.#scheduleButtonSync();
    this.#scheduleModelLoad();
    this.#loadShopifyXR();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#modelObserver?.disconnect();
  }

  #scheduleModelLoad = () => {
    this.#modelObserver?.disconnect();

    const model = this.querySelector('[data-product-ar-model-slot] product-model');
    if (!(model instanceof HTMLElement)) return;

    if (!('IntersectionObserver' in window)) {
      void this.#loadModel();
      return;
    }

    this.#modelObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.#modelObserver?.disconnect();
        void this.#loadModel();
      },
      { rootMargin: '300px 0px' }
    );
    this.#modelObserver.observe(model);
  };

  async #loadModel() {
    await customElements.whenDefined('product-model');
    if (!this.isConnected || this.hidden) return;

    const model = this.querySelector('[data-product-ar-model-slot] product-model');
    if (!(model instanceof HTMLElement) || !('loadContent' in model)) return;

    const loadContent = model.loadContent;
    if (typeof loadContent === 'function') loadContent.call(model, false);
    void this.#setupViewer();
  }

  async #setupViewer() {
    const viewer = await this.#waitForViewer();
    if (!viewer) return;

    // No camera / zoom overrides here: the old ones (4deg, 40%) re-enabled deep zoom (02/11/2026).
    this.#addWatermark();

    const start = () => {
      if (FRONT_VIEW_PRODUCTS.includes(this.dataset.productId || '')) {
        viewer.setAttribute('camera-orbit', '10deg 80deg 105%');
        viewer.jumpCameraToGoal?.();
      }

      const [animation] = viewer.availableAnimations || [];
      if (!animation) return;
      viewer.animationName = animation;
      viewer.pause();
      viewer.currentTime = Math.max(0, (viewer.duration || 0) - 0.05);
      setTimeout(() => {
        if (!viewer.isConnected) return;
        viewer.currentTime = 0;
        viewer.play();
      }, 2500);
    };
    if (viewer.loaded) {
      start();
    } else {
      viewer.addEventListener('load', start, { once: true, signal: this.#controller?.signal });
    }
  }

  /** Fixed Ohouse logo over the viewer so screenshots and screen recordings carry the brand. */
  #addWatermark() {
    const media = this.querySelector('.product-ar-showcase__media');
    if (!(media instanceof HTMLElement) || media.querySelector('.product-ar-showcase__watermark')) return;

    const logo = document.createElement('img');
    logo.className = 'product-ar-showcase__watermark';
    logo.src = new URL('ohouse-logo-3d.png', import.meta.url).href;
    logo.alt = '';
    logo.setAttribute('aria-hidden', 'true');
    logo.style.cssText =
      'position:absolute;inset-inline-start:16px;inset-block-start:16px;z-index:2;width:min(28%,140px);height:auto;opacity:.35;pointer-events:none;user-select:none';
    media.append(logo);
  }

  /** @returns {Promise<any>} */
  #waitForViewer() {
    const find = () => this.querySelector('[data-product-ar-model-slot] model-viewer');

    return new Promise((resolve) => {
      const found = find();
      if (found) return resolve(found);

      const observer = new MutationObserver(() => {
        const viewer = find();
        if (!viewer) return;
        observer.disconnect();
        resolve(viewer);
      });
      observer.observe(this, { childList: true, subtree: true });
      setTimeout(() => {
        observer.disconnect();
        resolve(find());
      }, 15000);
    });
  }

  #loadShopifyXR() {
    const modelJSON = this.querySelector('[data-product-ar-model-json]');
    if (!(modelJSON instanceof HTMLScriptElement) || !window.Shopify?.loadFeatures) return;

    Shopify.loadFeatures([
      {
        name: 'shopify-xr',
        version: '1.0',
        onLoad: (errors) => this.#setupShopifyXR(errors),
      },
    ]);
  }

  /** @param {Error[] | undefined} errors */
  #setupShopifyXR(errors) {
    if (errors) return;

    if (!window.ShopifyXR) {
      document.addEventListener('shopify_xr_initialized', () => this.#setupShopifyXR(), {
        once: true,
        signal: this.#controller?.signal,
      });
      return;
    }

    const modelJSON = this.querySelector('[data-product-ar-model-json]');
    if (!(modelJSON instanceof HTMLScriptElement) || modelJSON.dataset.shopifyXrLoaded === 'true') return;

    try {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent || '[]'));
      modelJSON.dataset.shopifyXrLoaded = 'true';
      window.ShopifyXR.setupXRElements();
    } catch (error) {
      console.warn('[product-ar-showcase] Shopify XR initialization failed:', error);
    }
  }

  #scheduleButtonSync = () => {
    if (this.#syncQueued) return;
    this.#syncQueued = true;

    requestAnimationFrame(() => {
      this.#syncQueued = false;
      this.#syncButtonVisibility();
    });
  };

  #syncButtonVisibility() {
    const arButton = this.querySelector('[data-product-ar-trigger]');
    if (!(arButton instanceof HTMLButtonElement)) return;

    const hasModel = this.querySelector('[data-product-ar-model-slot] product-model') instanceof HTMLElement;
    arButton.hidden = !hasModel;
  }

  /** @param {import('@theme/events').ProductArVariantUpdateEvent} event */
  #handleVariantUpdate = (event) => {
    const modelTemplate = event.detail.html.querySelector('template[data-product-ar-variant-template]');
    const modelSlot = this.querySelector('[data-product-ar-model-slot]');
    if (!(modelTemplate instanceof HTMLTemplateElement) || !(modelSlot instanceof HTMLElement)) return;

    this.#modelObserver?.disconnect();
    const modelContent = modelTemplate.content.querySelector('[data-product-ar-model-content]');
    if (!(modelContent instanceof HTMLElement)) {
      this.hidden = true;
      modelSlot.replaceChildren();
      this.querySelector('[data-product-ar-model-json]')?.remove();
      const arButton = this.querySelector('[data-product-ar-trigger]');
      if (arButton instanceof HTMLButtonElement) delete arButton.dataset.shopifyModel3dId;
      return;
    }

    const nextModelJSON = modelTemplate.content.querySelector('[data-product-ar-model-json]');
    const currentModelJSON = this.querySelector('[data-product-ar-model-json]');
    if (nextModelJSON instanceof HTMLScriptElement) {
      const clonedModelJSON = nextModelJSON.cloneNode(true);
      if (currentModelJSON) {
        currentModelJSON.replaceWith(clonedModelJSON);
      } else {
        this.prepend(clonedModelJSON);
      }
    }

    const modelNodes = Array.from(modelContent.childNodes, (node) => node.cloneNode(true));
    modelSlot.replaceChildren(...modelNodes);
    this.dataset.productId = modelTemplate.dataset.productId || this.dataset.productId;
    const arButton = this.querySelector('[data-product-ar-trigger]');
    if (arButton instanceof HTMLButtonElement && modelContent.dataset.modelId) {
      arButton.dataset.shopifyModel3dId = modelContent.dataset.modelId;
    }
    this.hidden = false;
    this.#loadShopifyXR();
    this.#scheduleButtonSync();
    this.#scheduleModelLoad();
  };
}

if (!customElements.get('product-ar-showcase')) {
  customElements.define('product-ar-showcase', ProductArShowcase);
}
